import { useState, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getUserByPhone, updateUser } from '../../utils/sharedStorage';

const ADMIN_NUMBERS = ['97800237', '92242664', '92252664', '98436291', '90804284', '70636321'];

const ADMIN_PASSWORDS: Record<string, string> = {
  '97800237': '123640',
  '92242664': '123640',
  '92252664': 'eriCA@31',
  '98436291': 'eriCA@31',
  '90804284': 'Achraf90',
  '70636321': 'Justin2001',
};

const DUAL_ACCESS_ADMIN = '98436291';

// Durée de session : 7 jours
const SESSION_DURATION = 7 * 24 * 60 * 60 * 1000;

export default function Connexion() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ phone: '', password: '' });
  const [error, setError] = useState('');
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const createUserSession = (phone: string) => {
    const sessionExpiry = Date.now() + SESSION_DURATION;
    const userToken = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('userSessionToken', userToken);
    sessionStorage.setItem('sessionUserId', phone);
    sessionStorage.setItem('sessionExpiry', sessionExpiry.toString());
    localStorage.setItem('sessionExpiry', sessionExpiry.toString());
    localStorage.setItem('sessionUserId', phone);
    localStorage.setItem('userSessionToken', userToken);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    const phoneClean = formData.phone.trim().replace(/\D/g, '');
    const passwordClean = formData.password.trim();

    if (!phoneClean || !passwordClean) {
      setError('Veuillez remplir tous les champs');
      return;
    }

    setIsLoading(true);

    try {
      // ── Vérification admin ──────────────────────────────────────────────
      if (ADMIN_NUMBERS.includes(phoneClean)) {
        const expectedPassword = ADMIN_PASSWORDS[phoneClean];
        if (passwordClean !== expectedPassword) {
          setError('Mot de passe administrateur incorrect.');
          setIsLoading(false);
          return;
        }

        const sessionExpiry = Date.now() + SESSION_DURATION;
        const adminToken = `admin_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        sessionStorage.setItem('adminSessionToken', adminToken);
        sessionStorage.setItem('adminPhone', phoneClean);
        sessionStorage.setItem('adminPassword', passwordClean);
        sessionStorage.setItem('sessionExpiry', sessionExpiry.toString());
        sessionStorage.setItem('isAdmin', 'true');
        sessionStorage.setItem('adminSession', JSON.stringify({ isAdmin: true, phone: phoneClean }));
        localStorage.setItem('userPhone', phoneClean);
        localStorage.setItem('sessionExpiry', sessionExpiry.toString());

        if (phoneClean === DUAL_ACCESS_ADMIN) {
          // ✅ Chercher dans Supabase en priorité
          const user = await getUserByPhone(phoneClean);
          if (user) {
            // ✅ Synchroniser Supabase → localStorage
            localStorage.setItem('userName', user.name || '');
            localStorage.setItem('userCountry', user.country || '');
            localStorage.setItem('userUsername', user.username || '');
            localStorage.setItem('currentUser', JSON.stringify({ ...user, isActivated: true, accountStatus: 'active' }));
            localStorage.setItem(`user_${phoneClean}`, JSON.stringify(user));
            createUserSession(phoneClean);
            
            // ✅ Mettre à jour lastLogin dans Supabase
            try { 
              await updateUser(phoneClean, { lastLogin: new Date().toISOString() }); 
            } catch (err) { 
              console.warn('Erreur mise à jour lastLogin:', err);
            }
            
            setIsLoading(false);
            navigate('/tableau-de-bord');
            return;
          }
        }

        setIsLoading(false);
        navigate('/admin');
        return;
      }

      // ── Connexion utilisateur normal (Supabase prioritaire) ──────────
      // ✅ Chercher dans Supabase en priorité
      const user = await getUserByPhone(phoneClean);

      if (!user) {
        setError('Numéro non trouvé. Vérifiez votre numéro ou créez un compte.');
        setIsLoading(false);
        return;
      }

      if (user.blocked) {
        setError('Votre compte a été bloqué. Contactez le service client.');
        setIsLoading(false);
        return;
      }

      const storedPassword = (user.password || '').trim();
      if (passwordClean !== storedPassword) {
        setError('Mot de passe incorrect. Vérifiez et réessayez.');
        setIsLoading(false);
        return;
      }

      // ── Session créée + Synchronisation Supabase → localStorage ───────
      localStorage.setItem('userPhone', phoneClean);
      localStorage.setItem('userName', user.name || '');
      localStorage.setItem('userCountry', user.country || '');
      localStorage.setItem('userUsername', user.username || '');
      localStorage.setItem('currentUser', JSON.stringify(user));
      localStorage.setItem(`user_${phoneClean}`, JSON.stringify(user));
      createUserSession(phoneClean);

      // ✅ Mettre à jour lastLogin dans Supabase
      try { 
        await updateUser(phoneClean, { lastLogin: new Date().toISOString() }); 
      } catch (err) { 
        console.warn('Erreur mise à jour lastLogin:', err);
      }

      setIsLoading(false);

      const isActivated = user.isActivated === true || user.accountStatus === 'active';
      if (!isActivated) {
        navigate('/paiement-activation');
      } else {
        navigate('/tableau-de-bord');
      }
    } catch (err) {
      console.error('Erreur connexion:', err);
      setError('Erreur de connexion. Vérifiez votre connexion internet et réessayez.');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ fontFamily: "'Inter', sans-serif" }}>
      
      {/* ===== PANNEAU GAUCHE ===== */}
      <div className="hidden lg:flex lg:w-[45%] relative overflow-hidden flex-col items-center justify-center p-12"
        style={{ background: 'linear-gradient(145deg, #1a0a0a 0%, #3d0c0c 40%, #7f1d1d 100%)' }}>
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-[-100px] right-[-100px] w-80 h-80 rounded-full border border-red-800/30"></div>
          <div className="absolute top-[-60px] right-[-60px] w-56 h-56 rounded-full border border-red-700/20"></div>
          <div className="absolute bottom-[-80px] left-[-80px] w-72 h-72 rounded-full border border-red-800/30"></div>
          <div className="absolute bottom-20 right-10 w-32 h-32 rounded-full bg-red-900/20"></div>
          <div className="absolute top-1/3 left-[-40px] w-24 h-24 rounded-full bg-orange-900/20"></div>
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.04) 1px, transparent 1px)',
            backgroundSize: '32px 32px'
          }}></div>
        </div>

        <div className="relative z-10 text-center w-full max-w-sm">
          <div className="relative mx-auto mb-8 w-24 h-24">
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-red-500/30 to-orange-500/20 blur-xl"></div>
            <div className="relative w-24 h-24 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/10 shadow-2xl">
              <img
                src="https://static.readdy.ai/image/82792080bea9235a3a5c58a151cce2b4/fff4bb4e89a9e983dddf5fffb9dae068.png"
                alt="SUCCESSLINK AGENCY"
                className="w-16 h-16 object-contain"
              />
            </div>
          </div>

          <h1 className="text-3xl font-black text-white mb-1 tracking-tight">SUCCESSLINK</h1>
          <div className="inline-flex items-center gap-2 bg-red-500/20 border border-red-500/30 rounded-full px-4 py-1 mb-10">
            <div className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse"></div>
            <span className="text-red-300 text-xs font-semibold tracking-widest uppercase">Agency</span>
          </div>

          <div className="space-y-3 text-left">
            {[
              { icon: 'ri-dashboard-line', title: 'Tableau de bord', desc: 'Accédez à vos statistiques' },
              { icon: 'ri-money-dollar-circle-line', title: 'Revenus en temps réel', desc: 'Suivez vos gains quotidiens' },
              { icon: 'ri-team-line', title: 'Réseau de filleuls', desc: 'Gérez votre communauté' },
              { icon: 'ri-wallet-3-line', title: 'Retraits faciles', desc: 'Récupérez vos gains' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-4 rounded-xl px-4 py-3 bg-white/5 border border-white/8 hover:bg-white/8 transition-all duration-200">
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-gradient-to-br from-red-500/30 to-orange-500/20 flex-shrink-0">
                  <i className={`${item.icon} text-red-300 text-base`}></i>
                </div>
                <div>
                  <p className="text-white text-sm font-semibold">{item.title}</p>
                  <p className="text-white/40 text-xs">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 pt-8 border-t border-white/10">
            <p className="text-white/30 text-xs">© 2025 SUCCESSLINK AGENCY</p>
          </div>
        </div>
      </div>

      {/* ===== PANNEAU DROIT ===== */}
      <div className="w-full lg:w-[55%] flex flex-col items-center justify-center min-h-screen px-6 py-10"
        style={{ background: '#fafafa' }}>

        <div className="lg:hidden flex flex-col items-center mb-8">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-3 shadow-lg"
            style={{ background: 'linear-gradient(135deg, #dc2626, #ea580c)' }}>
            <img
              src="https://static.readdy.ai/image/82792080bea9235a3a5c58a151cce2b4/fff4bb4e89a9e983dddf5fffb9dae068.png"
              alt="SUCCESSLINK AGENCY"
              className="w-10 h-10 object-contain"
            />
          </div>
          <h1 className="text-xl font-black text-gray-900 tracking-tight">SUCCESSLINK</h1>
          <p className="text-gray-400 text-xs font-medium">AGENCY</p>
        </div>

        <div className="w-full max-w-[420px]">
          <div className="mb-8">
            <h2 className="text-2xl font-black text-gray-900 mb-1">Bon retour 👋</h2>
            <p className="text-gray-400 text-sm">Connectez-vous pour accéder à votre espace</p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-2xl flex items-start gap-3">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-error-warning-fill text-red-500 text-lg"></i>
              </div>
              <div className="flex-1">
                <p className="text-sm text-red-700 font-medium">{error}</p>
                {error.includes('Numéro non trouvé') && (
                  <Link to="/inscription" className="text-xs text-red-500 underline mt-1 block font-semibold">
                    → Créer un compte gratuitement
                  </Link>
                )}
                {error.includes('Mot de passe incorrect') && (
                  <Link to="/mot-de-passe-oublie" className="text-xs text-red-500 underline mt-1 block font-semibold">
                    → Mot de passe oublié ?
                  </Link>
                )}
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Numéro de téléphone</label>
              <div className={`flex items-center rounded-xl border-2 transition-all duration-200 ${
                focusedField === 'phone' ? 'border-red-500 bg-white shadow-lg shadow-red-50' : 'border-gray-200 bg-white'
              }`}>
                <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-line text-gray-400 text-lg"></i>
                </div>
                <input
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  onFocus={() => setFocusedField('phone')}
                  onBlur={() => setFocusedField(null)}
                  className="flex-1 py-3.5 pr-4 bg-transparent outline-none text-sm text-gray-800 placeholder-gray-300"
                  placeholder="Ex: 07 00 00 00 00"
                />
              </div>
              <p className="text-xs text-gray-400 mt-1 ml-1">Entrez votre numéro sans indicatif pays</p>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider">Mot de passe</label>
                <Link to="/mot-de-passe-oublie" className="text-xs text-red-500 hover:text-red-600 font-semibold cursor-pointer">
                  Oublié ?
                </Link>
              </div>
              <div className={`flex items-center rounded-xl border-2 transition-all duration-200 ${
                focusedField === 'password' ? 'border-red-500 bg-white shadow-lg shadow-red-50' : 'border-gray-200 bg-white'
              }`}>
                <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                  <i className="ri-lock-password-line text-gray-400 text-lg"></i>
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  onFocus={() => setFocusedField('password')}
                  onBlur={() => setFocusedField(null)}
                  className="flex-1 py-3.5 bg-transparent outline-none text-sm text-gray-800 placeholder-gray-300"
                  placeholder="Votre mot de passe"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="w-12 h-12 flex items-center justify-center text-gray-300 hover:text-red-500 cursor-pointer transition"
                >
                  <i className={showPassword ? 'ri-eye-off-line text-lg' : 'ri-eye-line text-lg'}></i>
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full text-white font-bold py-4 rounded-xl transition-all duration-200 shadow-lg disabled:opacity-60 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 text-sm mt-2"
              style={{ background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)' }}
            >
              {isLoading ? (
                <>
                  <i className="ri-loader-4-line animate-spin text-lg"></i>
                  Connexion en cours...
                </>
              ) : (
                <>
                  Se connecter
                  <i className="ri-arrow-right-line text-lg"></i>
                </>
              )}
            </button>
          </form>

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-gray-200"></div>
            <span className="text-xs text-gray-300 font-medium px-2">OU</span>
            <div className="flex-1 h-px bg-gray-200"></div>
          </div>

          <Link
            to="/inscription"
            className="w-full flex items-center justify-center gap-2 py-3.5 border-2 border-gray-200 text-gray-600 font-semibold rounded-xl hover:border-red-200 hover:text-red-600 hover:bg-red-50/50 transition-all duration-200 cursor-pointer text-sm"
          >
            <i className="ri-user-add-line text-base"></i>
            Créer un compte gratuitement
          </Link>

          <p className="text-center text-xs text-gray-300 mt-8 flex items-center justify-center gap-1.5">
            <i className="ri-shield-check-line"></i>
            Connexion sécurisée — Données protégées
          </p>
        </div>
      </div>
    </div>
  );
}